import { Fragment, ReactElement, Ref, cloneElement, useCallback } from 'react';

import { useImpressionTracker } from '../../hooks';
import { ElementId, TrackingData } from '../../types';
import { TrackingScope } from '../TrackingScope';

export interface ImpressionTrackerProps {
  elementId: ElementId;
  data?: TrackingData;
  children: ReactElement;
  scope?: boolean;
}

function setRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref && typeof ref === 'object') {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

export function ImpressionTracker({
  elementId,
  data,
  children,
  scope = false,
}: ImpressionTrackerProps) {
  if (process.env.NODE_ENV === 'development' && children.type === Fragment) {
    // eslint-disable-next-line no-console
    console.error(
      'ImpressionTracker: children must be a single DOM element, not a Fragment. ' +
        'Wrap the children in a container element so the ref can be attached.',
    );
  }

  const impressionRef = useImpressionTracker({ elementId, data });
  const childRef = (children as any).ref;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mergedRef = useCallback(
    (node: Element | null) => {
      setRef(impressionRef as Ref<Element>, node);
      setRef(childRef, node);
    },
    [impressionRef, childRef],
  );

  const tracked = cloneElement(children, { ...children.props, ref: mergedRef });

  if (scope) {
    return (
      <TrackingScope elementId={elementId} data={data}>
        {tracked}
      </TrackingScope>
    );
  }

  return tracked;
}
